import {Box, Button, Card, CardContent, Chip, Grid, Paper, Stack, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Typography} from "@mui/material";
import RepeatIcon from "@mui/icons-material/Repeat";
import {useTranslation} from "react-i18next";
import {formatDateNow} from "../../i18n/date-formatting";
import {timelineDescriptionLabel, timelineStatusLabel} from "../../courier/delivery-progress";
import {formatOrderMoney, singleOrderCurrency} from "./order-money";
import OrderDeliverySchedule, {DeliveryScheduleChange} from "./order-delivery-schedule";

function SupplierProducts({order, t, language}) {
    return <>
        <Typography variant="subtitle2" gutterBottom>{t("Items")}</Typography>
        <TableContainer component={Paper} variant="outlined" sx={{maxHeight: 400, mb: 2}}>
            <Table stickyHeader size="small" aria-label={t("ProductDetailsInformation")}>
                <TableHead><TableRow>
                    {["ProductCode", "ProductName", "Quantity", "UnitPrice", "TotalPrice"].map((key, index) =>
                        <TableCell key={key} align={index > 1 ? "right" : "left"}>{t(key)}</TableCell>)}
                </TableRow></TableHead>
                <TableBody>{order.orderProducts.map(item => <TableRow key={item.id} hover sx={{"&:nth-of-type(even)": {bgcolor: "grey.50"}}}>
                    <TableCell sx={{whiteSpace: "nowrap"}}>{item.productCode}</TableCell>
                    <TableCell sx={{minWidth: 200}}>{item.productName}</TableCell>
                    <TableCell align="right">{item.productCount}</TableCell>
                    <TableCell align="right" sx={{whiteSpace: "nowrap"}}>{formatOrderMoney(item.unitPrice, item.currency, language)}</TableCell>
                    <TableCell align="right" sx={{whiteSpace: "nowrap"}}>{formatOrderMoney(item.totalPrice, item.currency, language)}</TableCell>
                </TableRow>)}</TableBody>
            </Table>
        </TableContainer>
    </>;
}

function SupplierSummary({order, t, language}) {
    const currency = singleOrderCurrency(order.orderProducts);
    // Keep missing saved charges unknown rather than turning them into a zero total.
    const total = order.totalPrice == null || order.shippingCost == null ? null : order.totalPrice + order.shippingCost;
    return <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom>{t("OrderStatus")}</Typography>
            <Typography variant="body2"><strong>{t("Status")}:</strong> {t(order.status)}</Typography>
            {order.trackingNumber && <Typography variant="body2"><strong>{t("TrackingNumber")}:</strong> {order.trackingNumber}</Typography>}
            {order.estimatedDelivery && <Typography variant="body2"><strong>{t("EstimatedDelivery")}:</strong> {formatDateNow(order.estimatedDelivery, language, true)}</Typography>}
        </Grid>
        <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom>{t("OrderTotal")}</Typography>
            <Table size="small" aria-label={t("OrderTotal")}><TableBody>
                {[["Subtotal", order.totalPrice], ["ShippingCost", order.shippingCost], ["Total", total]].map(([key, value]) =>
                    <TableRow key={key} sx={key === "Total" ? {"& td": {fontWeight: 600}} : undefined}>
                        <TableCell>{t(key)}</TableCell><TableCell align="right">{formatOrderMoney(value, currency, language)}</TableCell>
                    </TableRow>)}
            </TableBody></Table>
        </Grid>
    </Grid>;
}

function SupplierTimeline({events, onOpenRevision, t, language, zone}) {
    if (!events?.length) {return null;}
    return <Box sx={{mt: 2}}>
        <Typography variant="subtitle2" gutterBottom>{t("Timeline")}</Typography>
        <Box sx={{borderLeft: 2, borderColor: "divider", ml: 1, pl: 3, py: 1}}>
            {events.map((event, index) => <Box key={index} sx={{position: "relative", mb: index === events.length - 1 ? 0 : 2,
                "&::before": {content: '""', position: "absolute", left: -30, top: 6, width: 10, height: 10, borderRadius: "50%", bgcolor: "primary.main"}}}>
                <Typography variant="subtitle2">{t(timelineStatusLabel(event))}</Typography>
                <Typography variant="caption" color="text.secondary">{formatDateNow(event.date, language, true)}</Typography>
                {event.description && !(event.description === "OrderShipmentRescheduled" && event.reason) &&
                    <Typography variant="body2" sx={{mt: 0.5}}>{t(timelineDescriptionLabel(event))}</Typography>}
                <DeliveryScheduleChange event={event} zone={zone}/>
                {event.proposalRevisionId && onOpenRevision && <Button size="small" onClick={() => onOpenRevision(event.proposalRevisionId)}>{t("ConfirmationReview")}</Button>}
            </Box>)}
        </Box>
    </Box>;
}

export default function PharmacyOrderSupplierDetails({order, statusInfo, onReorder, onOpenRevision}) {
    const {t, i18n} = useTranslation();
    return <Card variant="outlined"><CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" useFlexGap flexWrap="wrap" gap={2} sx={{mb: 2}}>
            <Stack direction="row" alignItems="center" useFlexGap flexWrap="wrap" gap={2}>
                <Typography variant="h6">{order.distributorName}</Typography>
                <Chip size="small" icon={statusInfo.icon} color={statusInfo.color} label={t(order.status)}/>
            </Stack>
            <Button size="small" variant="outlined" startIcon={<RepeatIcon/>} onClick={onReorder}>{t("Reorder")}</Button>
        </Stack>
        <OrderDeliverySchedule products={order.orderProducts} zone={order.deliveryTimeZone}/>
        <SupplierProducts order={order} t={t} language={i18n.language}/>
        <SupplierSummary order={order} t={t} language={i18n.language}/>
        <SupplierTimeline events={order.orderTimelines} onOpenRevision={onOpenRevision} t={t} language={i18n.language} zone={order.deliveryTimeZone}/>
    </CardContent></Card>;
}
